<template>
    <div class="mt-3">
        <v-row class="px-3">
            <v-col cols="12" align="center" v-if="prescriptions.length === 0">
                <div>
                    <template v-if="loading">
                        <loading-spinner no-margin-top />
                        <small>{{ $t('common.loading') }}...</small>
                    </template>
                    <small v-else>{{ $t('common.no_prescriptions_founded') }}</small>
                </div>
            </v-col>

            <v-col cols="12" v-for="prescription in prescriptions" :key="prescription.objectID" class="pa-4">
                <prescription-card :prescription="prescription" with-menu @click:edit="onEditPrescription(prescription)"
                    @click:copy="onCopyPrescription(prescription)"
                    @click:delete="showDeletePrescription(prescription.objectID)" />
            </v-col>
            <v-col cols="12" v-if="pageCount > 1">
                <kimsa-pagination @page="page = $event - 1" :pageCount="pageCount" no-right
                    :total-visible="isMobile ? 4 : (totalVisiblePagination || 7)" />
            </v-col>
        </v-row>

        <kimsa-confirm ref="showDelete" :title="$t('common.delete_prescription')"
            :content="$t('common.confirm_deleted_prescription')" :persistent="false" :loading="loadingDelete"
            @confirm="deletePrescription" @cancel="prescriptionId = null" :close-on-confirm="false"
            :alert-mode="true" />

        <create-prescription-dialog ref="createPrescriptionDialog" @success="loadPrescriptions" :coachId="coachId" />
    </div>
</template>

<script>

import PrescriptionCard from '@/components/common/routines/PrescriptionCard'
import { Client, PrescriptionIndex, UserIndex } from "@/utils/Algolia";
import { db } from "@/utils/Firebase";
import KimsaConfirm from "@/components/common/KimsaConfirm";
import { debounce } from "@/utils/Common";
import LoadingSpinner from "@/components/common/LoadingSpinner";
import KimsaPagination from "@/components/common/KimsaPagination";
import CreatePrescriptionDialog from './CreatePrescriptionDialog.vue';

export default {
    components: {
        KimsaPagination,
        LoadingSpinner,
        KimsaConfirm,
        PrescriptionCard,
        CreatePrescriptionDialog
    },
    props: {
        search: {
            type: String,
            default: '',
        },
        category: {
            type: String,
            default: '',
        },
        totalVisiblePagination: {
            type: [Number, String],
            default: null,
        }
    },
    data() {
        return {
            loading: false,
            prescriptions: [],
            prescriptionId: null,
            loadingDelete: false,
            coaches: [],
            page: 0,
            pageCount: 0,
            perPage: 6,
        }
    },
    computed: {
        isCompany(vm = this) {
            return vm.account?.accountType === 'company'
        },
        coachId(vm = this) {
            return vm.currentUser.uid
        }
    },
    async mounted() {
        let vm = this
        vm.isCompany ? await vm.loadCoaches() : await vm.loadPrescriptions()
    },
    methods: {
        async loadCoaches() {
            const vm = this
            let filter = `coachSubscription.companyId: ${vm.coachId} AND coachSubscription.paused != 1`
            await Client.clearCache()
            await UserIndex.search('', {
                filters: filter,
                hitsPerPage: 10000,
            })
                .then(({ hits }) => {
                    vm.coaches = [vm.currentUser.uid, ...hits.map(hit => (hit.objectID))]
                })
                .catch(err => {
                    console.log(err);
                });
        },
        async showDeletePrescription(id) {
            const vm = this
            vm.prescriptionId = id
            vm.$refs.showDelete.show()
        },
        async deletePrescription() {
            const vm = this
            vm.loadingDelete = true
            await db.collection('prescriptions').doc(vm.prescriptionId).delete()
            vm.prescriptions = vm.prescriptions.filter(prescription => prescription.objectID !== vm.prescriptionId)
            vm.toast(vm.$t('common.prescription_deleted'))
            vm.prescriptionId = null
            vm.$refs.showDelete.hide()
            vm.loadingDelete = false
        },
        debouncedLoadPrescriptions: debounce(
            function () {
                this.loadPrescriptions()
            }, 1000),
        async loadPrescriptions() {
            const vm = this
            vm.loading = true
            vm.prescriptions = []
            let coaches = vm.coaches.length === 0 ? vm.coachId : vm.coaches.join(' OR coachId:')
            let filter = `coachId: ${coaches}`
            if (vm.category) filter += ` AND category: ${vm.category}`
            await Client.clearCache()
            let response = await PrescriptionIndex.search(vm.search, {
                filters: filter,
                hitsPerPage: vm.perPage,
                page: vm.page,
            })
            vm.pageCount = response.nbPages || 0
            vm.prescriptions = response.hits
            vm.loading = false
        },
        refresh() {
            const vm = this;
            vm.loadPrescriptions()
        },
        onEditPrescription(prescription) {
            const vm = this
            vm.$refs.createPrescriptionDialog.setEditingMode(prescription)
        },
        async onCopyPrescription(prescription) {
            let vm = this
            let payload = {
                name: `${prescription.name} ${vm.$t('common.copy_template')}`,
                commentary: prescription.commentary,
                config: prescription.config,
                eachSide: prescription.eachSide,
                hasTime: prescription.hasTime,
                time: prescription.time,
                coachId: prescription.coachId,
                category: prescription.category,
                categoryName: prescription.categoryName,
            }
            if (prescription.maxWeight) payload.maxWeight = prescription.maxWeight
            await db.collection('prescriptions').add(payload)
            vm.toast(`${vm.$t('common.prescription_copied')}. ${vm.$t('common.delay_message')}`)
            vm.loadPrescriptions()
        },
        refresh() {
            const vm = this;
            vm.loadPrescriptions()
        }
    },
    watch: {
        coaches() {
            this.loadPrescriptions()
        },
        search(val) {
            this.page = 0
            if (!val) return this.loadPrescriptions()
            return this.debouncedLoadPrescriptions()
        },
        category() {
            this.loadPrescriptions()
        },
        page() {
            this.loadPrescriptions()
        },
        loading(val) {
            this.$emit('loading', val)
        },
    }
}
</script>

<style scoped></style>
